import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    failContract,
    isRecord,
    requireArray,
    requireKnownEnum,
    requireNumber,
    requireRecord,
    requireString,
    requireTimezoneAwareDateTime,
} from "./contractValidation";
import { buildMailClassificationDisplay } from "./mailClassificationDisplay";

dayjs.extend(relativeTime);

const DEFAULT_PAGE = 1;
const UNKNOWN_SUBJECT = "无主题";
const UNKNOWN_SENDER = "未知发件人";

const PROCESSING_STATUS_LANGUAGE = {
    new: { label: "新邮件", tone: "neutral" },
    normalized: { label: "已归一化", tone: "processing" },
    classified: { label: "已分类", tone: "success" },
    dispatched: { label: "已分派", tone: "success" },
    skipped: { label: "已跳过处理", tone: "neutral" },
    failed: { label: "处理异常", tone: "danger" },
    retried: { label: "已重试", tone: "warning" },
    terminated: { label: "已终止", tone: "neutral" },
};

const BUSINESS_STATUS_LANGUAGE = {
    confirmed: { label: "已确认关联", tone: "success" },
    pending_review: { label: "待确认关联", tone: "warning" },
    rejected: { label: "关联已排除", tone: "neutral" },
    unknown: { label: "关联状态待核对", tone: "neutral" },
    conflict: { label: "关联冲突", tone: "danger" },
    mention: { label: "仅提及", tone: "neutral" },
};

const AG_REVIEW_LIFECYCLES = new Set(["open", "closed"]);

function toRecord(value) {
    return isRecord(value) ? value : {};
}

function normalizeWhitespace(value) {
    return String(value).replace(/\s+/g, " ").trim();
}

function normalizeCharset(charset) {
    const normalized = normalizeWhitespace(charset || "utf-8").toLowerCase();
    if (normalized === "utf8") return "utf-8";
    return normalized;
}

function decodeBytes(bytes, charset) {
    try {
        return new TextDecoder(normalizeCharset(charset), { fatal: false }).decode(new Uint8Array(bytes));
    } catch {
        return String.fromCharCode(...bytes);
    }
}

function decodeBase64Word(encodedText, charset) {
    try {
        const binary = atob(encodedText.replace(/\s/g, ""));
        const bytes = Array.from(binary, (char) => char.charCodeAt(0));
        return decodeBytes(bytes, charset);
    } catch {
        return encodedText;
    }
}

function decodeQuotedPrintableWord(encodedText, charset) {
    const bytes = [];
    for (let index = 0; index < encodedText.length; index += 1) {
        const char = encodedText[index];
        if (char === "_") {
            bytes.push(32);
            continue;
        }
        if (char === "=" && /^[0-9a-fA-F]{2}$/.test(encodedText.slice(index + 1, index + 3))) {
            bytes.push(parseInt(encodedText.slice(index + 1, index + 3), 16));
            index += 2;
            continue;
        }
        bytes.push(char.charCodeAt(0));
    }
    return decodeBytes(bytes, charset);
}

export function decodeMimeEncodedWords(value) {
    if (typeof value !== "string") return "";

    const compactAdjacentWords = value.replace(
        /(=\?[^?]+\?[bBqQ]\?[^?]*\?=)\s+(?==\?[^?]+\?[bBqQ]\?[^?]*\?=)/g,
        "$1",
    );

    return compactAdjacentWords.replace(/=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g, (matched, charset, encoding, encodedText) => {
        if (encoding.toUpperCase() === "B") {
            return decodeBase64Word(encodedText, charset);
        }
        if (encoding.toUpperCase() === "Q") {
            return decodeQuotedPrintableWord(encodedText, charset);
        }
        return matched;
    });
}

function normalizeText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    const decoded = normalizeWhitespace(decodeMimeEncodedWords(String(value)));
    return decoded || fallback;
}

function formatShortId(value) {
    const text = normalizeText(value);
    if (!text) return "";
    return text.length > 12 ? `${text.slice(0, 8)}…${text.slice(-4)}` : text;
}

function buildTime(value, path) {
    const parsed = requireTimezoneAwareDateTime(value, path);
    return {
        value: parsed,
        relativeText: parsed.locale("zh-cn").fromNow(),
        absoluteText: parsed.format("YYYY-MM-DD HH:mm"),
    };
}

function buildSender(message = {}) {
    const name = normalizeText(message.from_display);
    const address = normalizeText(message.from_address);
    const label = name && address ? `${name} <${address}>` : name || address || UNKNOWN_SENDER;

    return { name, address, label };
}

function buildMailboxLabel(receipt = {}) {
    const displayName = normalizeText(receipt.mailbox_display_name);
    const address = normalizeText(receipt.mailbox_email_address);
    if (displayName && address) return `${displayName} <${address}>`;
    return displayName || address || "未标明邮箱";
}

function mapProcessingStatus(status, path) {
    return requireKnownEnum(status, PROCESSING_STATUS_LANGUAGE, path).value;
}

function mapBusinessStatus(status, path) {
    return requireKnownEnum(status, BUSINESS_STATUS_LANGUAGE, path).value;
}

function buildAgReviewSignal(agReviewCases, path) {
    const collection = requireRecord(agReviewCases, path);
    const pendingCount = requireNumber(collection.pending_count, `${path}.pending_count`, { integer: true, min: 0 });
    const sourceCount = requireNumber(collection.source_count, `${path}.source_count`, { integer: true, min: 0 });
    const closedCount = requireNumber(collection.closed_count, `${path}.closed_count`, { integer: true, min: 0 });
    const unknownCount = requireNumber(collection.unknown_count, `${path}.unknown_count`, { integer: true, min: 0 });
    let lifecycle = "";

    if (collection.latest !== null && collection.latest !== undefined) {
        const latest = requireRecord(collection.latest, `${path}.latest`);
        lifecycle = normalizeText(latest.review_case_lifecycle).toLowerCase();
        if (!AG_REVIEW_LIFECYCLES.has(lifecycle)) {
            failContract("响应契约错误", `${path}.latest.review_case_lifecycle 返回未知枚举 ${lifecycle || "<empty>"}`);
        }
    }

    if (unknownCount > 0) {
        failContract("响应契约错误", `${path}.unknown_count 表示存在未知审核生命周期`);
    }

    if (pendingCount > 0 || lifecycle === "open") {
        return {
            visible: true,
            label: pendingCount > 0 ? `${pendingCount} 项待确认` : "有待确认判断",
            tone: "warning",
        };
    }

    if (sourceCount > 0) {
        return {
            visible: true,
            label: closedCount > 0 && unknownCount === 0 ? "判断已关闭" : "有判断记录",
            tone: "neutral",
        };
    }

    return { visible: false, label: "", tone: "neutral" };
}

function buildBusinessObject(entity, index, path) {
    const record = requireRecord(entity, path);
    const entityType = requireString(record.entity_type, `${path}.entity_type`);
    const entityId = requireString(record.entity_id, `${path}.entity_id`);
    const snapshot = toRecord(entity.entity_snapshot);
    const typeLabel = normalizeText(
        record.entity_type_label
            || record.entity_label
            || record.display_type,
    );
    if (!typeLabel) {
        failContract("响应契约错误", `${path}.entity_type 缺少可展示业务对象类型标签`);
    }
    const objectLabel = normalizeText(
        record.entity_display_name
            || record.display_name
            || record.name
            || record.title
            || formatShortId(entityId),
    );
    const status = mapBusinessStatus(record.product_status, `${path}.product_status`);
    const label = objectLabel && objectLabel !== typeLabel ? `${typeLabel} · ${objectLabel}` : typeLabel;

    return {
        key: `${normalizeText(entityType, "entity")}:${normalizeText(entityId, String(index))}:${index}`,
        entityType,
        entityId,
        snapshot,
        links: [],
        label,
        linkCount: requireNumber(record.link_count, `${path}.link_count`, { integer: true, min: 0 }),
        status,
    };
}

function buildBusinessObjects(item, path) {
    const businessContext = requireRecord(item.business_context, `${path}.business_context`);
    return requireArray(businessContext.business_entities, `${path}.business_context.business_entities`)
        .map((entity, index) => buildBusinessObject(entity, index, `${path}.business_context.business_entities[${index}]`));
}

function buildConversationChips(item, businessObjects, agReviewSignal, path) {
    const conversation = requireRecord(item.conversation, `${path}.conversation`);
    const attachmentSummary = requireRecord(item.attachment_summary, `${path}.attachment_summary`);
    const receiptAwareness = requireRecord(item.receipt_awareness, `${path}.receipt_awareness`);
    const messageCount = requireNumber(conversation.message_count, `${path}.conversation.message_count`, { integer: true, min: 0 });
    const receiptCount = requireNumber(conversation.receipt_count, `${path}.conversation.receipt_count`, { integer: true, min: 0 });
    const mailboxCount = requireNumber(conversation.mailbox_count, `${path}.conversation.mailbox_count`, { integer: true, min: 0 });
    const attachmentCount = requireNumber(attachmentSummary.count, `${path}.attachment_summary.count`, { integer: true, min: 0 });
    const inlineImageCount = requireNumber(attachmentSummary.inline_image_count, `${path}.attachment_summary.inline_image_count`, { integer: true, min: 0 });
    const processingStatus = mapProcessingStatus(receiptAwareness.latest_processing_status, `${path}.receipt_awareness.latest_processing_status`);
    const classification = buildMailClassificationDisplay(item.classification);

    const chips = [
        { key: "processing", label: processingStatus.label, tone: processingStatus.tone },
    ];

    if (classification.visible) {
        chips.push({ key: "classification", label: classification.label, tone: "accent" });
    }

    chips.push({ key: "messages", label: `${messageCount} 封邮件`, tone: "neutral" });

    if (receiptCount > 1) {
        chips.push({ key: "receipts", label: `${receiptCount} 个邮箱副本`, tone: "neutral" });
    }

    if (mailboxCount > 1) {
        chips.push({ key: "mailboxes", label: `${mailboxCount} 个收件邮箱`, tone: "neutral" });
    }

    if (attachmentSummary.has_attachments || attachmentCount > 0) {
        chips.push({
            key: "attachments",
            label: attachmentCount > 0 ? `${attachmentCount} 个附件` : "有附件",
            tone: "accent",
            icon: "attachment",
        });
    }

    if (inlineImageCount > 0) {
        chips.push({ key: "inline-images", label: `${inlineImageCount} 张内嵌图片`, tone: "neutral" });
    }

    if (businessObjects.length > 0) {
        chips.push({ key: "business", label: `${businessObjects.length} 个业务对象`, tone: "accent" });
    }

    if (agReviewSignal.visible) {
        chips.push({ key: "ag-review", label: agReviewSignal.label, tone: agReviewSignal.tone });
    }

    return chips;
}

export function buildInboxConversationItemViewModel(item, index = 0) {
    const path = `items[${index}]`;
    const record = requireRecord(item, path);
    const conversation = requireRecord(record.conversation, `${path}.conversation`);
    const latestMessage = requireRecord(record.latest_message, `${path}.latest_message`);
    const latestReceipt = requireRecord(record.latest_receipt, `${path}.latest_receipt`);
    const attachmentSummary = requireRecord(record.attachment_summary, `${path}.attachment_summary`);
    const businessContext = requireRecord(record.business_context, `${path}.business_context`);
    const businessObjects = buildBusinessObjects(record, path);
    const agReviewSignal = buildAgReviewSignal(businessContext.ag_review_cases, `${path}.business_context.ag_review_cases`);
    const latestTime = buildTime(conversation.latest_activity_at, `${path}.conversation.latest_activity_at`);
    const subject = normalizeText(conversation.subject || latestMessage.subject, UNKNOWN_SUBJECT);
    const summary = normalizeText(latestMessage.summary);
    const key = requireString(conversation.key, `${path}.conversation.key`);
    const threadId = normalizeText(conversation.thread_id);
    const latestReceiptId = normalizeText(latestReceipt.id);

    if (!threadId && !latestReceiptId) {
        failContract("响应契约错误", `${path} 缺少可打开的 thread_id 或 latest_receipt.id`);
    }

    return {
        key,
        threadId,
        latestReceiptId,
        subject,
        sender: buildSender(latestMessage),
        latestTime,
        summary,
        mailbox: {
            id: normalizeText(latestReceipt.mailbox_id),
            label: buildMailboxLabel(latestReceipt),
        },
        counts: {
            messages: requireNumber(conversation.message_count, `${path}.conversation.message_count`, { integer: true, min: 0 }),
            receipts: requireNumber(conversation.receipt_count, `${path}.conversation.receipt_count`, { integer: true, min: 0 }),
            mailboxes: requireNumber(conversation.mailbox_count, `${path}.conversation.mailbox_count`, { integer: true, min: 0 }),
            attachments: requireNumber(attachmentSummary.count, `${path}.attachment_summary.count`, { integer: true, min: 0 }),
            inlineImages: requireNumber(attachmentSummary.inline_image_count, `${path}.attachment_summary.inline_image_count`, { integer: true, min: 0 }),
        },
        businessObjects,
        visibleBusinessObjects: businessObjects.slice(0, 3),
        businessObjectOverflowCount: Math.max(businessObjects.length - 3, 0),
        chips: buildConversationChips(record, businessObjects, agReviewSignal, path),
    };
}

export function buildInboxConversationListViewModel(payload) {
    const record = requireRecord(payload, "inboxConversationList");
    const rawItems = requireArray(record.items, "inboxConversationList.items");
    const items = rawItems.map(buildInboxConversationItemViewModel);
    const pagination = requireRecord(record.pagination, "inboxConversationList.pagination");
    const pageSize = requireNumber(pagination.pageSize, "inboxConversationList.pagination.pageSize", { integer: true, min: 1 });
    const total = requireNumber(pagination.total, "inboxConversationList.pagination.total", { integer: true, min: 0 });

    return {
        items,
        pagination: {
            page: requireNumber(pagination.page, "inboxConversationList.pagination.page", { integer: true, min: DEFAULT_PAGE }),
            pageSize,
            total,
            totalPages: requireNumber(pagination.totalPages, "inboxConversationList.pagination.totalPages", { integer: true, min: 0 }),
        },
    };
}

export function buildInboxMailboxOptions(mailboxes) {
    return requireArray(mailboxes, "inboxMailboxes.mailboxes").map((mailbox, index) => {
        const record = requireRecord(mailbox, `inboxMailboxes.mailboxes[${index}]`);
        const displayName = normalizeText(record.display_name);
        const address = normalizeText(record.email_address);
        const id = requireString(record.id, `inboxMailboxes.mailboxes[${index}].id`);
        if (!displayName && !address) {
            failContract("响应契约错误", `inboxMailboxes.mailboxes[${index}] 缺少邮箱显示名或地址`);
        }
        return {
            label: displayName && address ? `${displayName} <${address}>` : displayName || address,
            value: id,
        };
    });
}
